import { ComponentWithOwnLabel } from 'Components/Core/types';
import { InterfaceConnectionPromptIssuer } from 'Components/Equipment/Interfacing';
import { IEquipmentDataModel } from 'Data/ViewModels/Equipment';
import { Guid } from 'Lib/Guid';
import { ListDropdown } from '..';
import { EquipmentLabel, IEquipmentLabelProps } from '../../Equipment/EquipmentLabel';
import EquipmentTypes from '../../Equipment/equipmentTypes';
import { IListDropdownProps } from '../ListDropdown';

export interface IStandaloneEquipmentDropdownProps extends IListDropdownProps {
  // Equipment data fetched by the wrapper field StandaloneEquipmentDropdownField
  equipment?: IEquipmentDataModel;

  // These props should be compulsory, but need to be optional due to late bound field props
  equipmentType?: string;
  laboratoryId?: Guid;
}

/**
 * Provides an equipment selection where equipment has been fetched independently of any other context
 * (That is, the equipment is not obtained as part of a larger model such as a worksheet)
 *
 * Equipment is provided via a property by another component that fetches the required data .
 * (i.e. StandaloneEquipmentDropdownField)
 */
const StandaloneEquipmentDropdown: ComponentWithOwnLabel<IStandaloneEquipmentDropdownProps, IEquipmentLabelProps> = ({
  laboratoryId,
  maxResults = 20,
  value = null,
  equipmentType,
  onChange,
  placeholder = 'Select equipment...',
  queryParams,
  disabled,
  equipment,
  ...otherProps
}) => {
  if (!equipmentType)
    throw new Error('Component StandaloneEquipmentDropdown requires an equipmentType to be specified.');
  const equipmentOnChange = (evt: any) => {
    evt.data.value =
      evt.data.value === null
        ? null
        : otherProps.simpleValue
        ? evt.data.value
        : { Id: evt.data.value.value, Code: evt.data.value.label };
    onChange?.(evt);
  };

  const endpoint = (EquipmentTypes as any)[equipmentType]?.endpoint ?? 'list.equipment';

  return (
    <>
      <InterfaceConnectionPromptIssuer equipment={equipment} equipmentType={equipmentType} disabled={disabled} />
      <ListDropdown
        {...otherProps}
        disabled={disabled}
        placeholder={placeholder}
        endpoint={endpoint}
        onChange={equipmentOnChange}
        queryParams={{ ...queryParams, equipmentType, laboratoryId, limit: maxResults }}
        value={
          value &&
          (otherProps.simpleValue ? value : value.Id || value.Code ? { value: value.Id, label: value.Code } : null)
        }
        maxResults={maxResults}
      />
    </>
  );
};

StandaloneEquipmentDropdown.Label = EquipmentLabel;

export { StandaloneEquipmentDropdown };
